import { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation } from "convex/react";
import { useLocation } from "@tanstack/react-router";
import { api } from "../../../convex/_generated/api";
import MonthView from "./MonthView.tsx";
import WeekView from "./WeekView.tsx";
import DayView from "./DayView.tsx";

type ViewType = "month" | "week" | "day";

export default function AgendaPage() {
    const barbers = useQuery(api.index.barbers_list) ?? [];
    const clients = useQuery(api.index.clients_list) ?? [];
    const createAppointment = useMutation(
        api.index.appointments_createOrUpdate,
    );
    const clientsById = useMemo(
        () => new Map(clients.map((c) => [c._id, c])),
        [clients],
    );
    const barbersById = useMemo(
        () => new Map(barbers.map((b) => [b._id, b])),
        [barbers],
    );

    const [view, setView] = useState<ViewType>("month");
    const [barberId, setBarberId] = useState<string | "todos">("todos");
    const location = useLocation();

    // Force month view when coming from home/logo click
    useEffect(() => {
        if (location.pathname === "/agenda") {
            setView("month");
        }
    }, [location.pathname]);
    const toMidnight = (d: Date) =>
        new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());

    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalDate, setModalDate] = useState<Date>(new Date());
    const [modalTime, setModalTime] = useState<string>("09:00");
    const [modalBarberId, setModalBarberId] = useState<string>(
        barbers[0]?._id ?? "",
    );
    const [modalClientId, setModalClientId] = useState<string>(
        clients[0]?._id ?? "",
    );
    const [modalService, setModalService] = useState<string>("Corte de cabelo");
    const [modalDuration, setModalDuration] = useState<number>(60);
    const [modalNotes, setModalNotes] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const serviceOptions = [
        "Corte de cabelo",
        "Barba",
        "Corte + Barba",
        "Bigode",
        "Sobrancelha",
        "Relaxamento",
        "Coloração",
        "Tratamento capilar",
    ];

    // Get data based on view
    const getDateRange = () => {
        const date = new Date(selectedDate);
        switch (view) {
            case "month":
                const monthStart = new Date(
                    date.getFullYear(),
                    date.getMonth(),
                    1,
                );
                const monthEnd = new Date(
                    date.getFullYear(),
                    date.getMonth() + 1,
                    0,
                );
                return {
                    start: toMidnight(monthStart),
                    end: toMidnight(monthEnd) + 24 * 60 * 60 * 1000,
                };
            case "week":
                const startOfWeek = new Date(date);
                startOfWeek.setDate(date.getDate() - date.getDay());
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                return {
                    start: toMidnight(startOfWeek),
                    end: toMidnight(endOfWeek) + 24 * 60 * 60 * 1000,
                };
            case "day":
                return {
                    start: toMidnight(date),
                    end: toMidnight(date) + 24 * 60 * 60 * 1000,
                };
            default:
                return {
                    start: toMidnight(date),
                    end: toMidnight(date) + 24 * 60 * 60 * 1000,
                };
        }
    };

    const { start, end } = getDateRange();
    const appts = useQuery(api.index.appointments_listForDay, {
        dateStartMs: start,
        dateEndMs: end,
        barberId: barberId === "todos" ? undefined : (barberId as any),
    });

    const sortedAppts = (appts ?? [])
        .slice()
        .sort((a, b) => a.startTime - b.startTime);

    const totalAppts = sortedAppts.length;
    const completedAppts = sortedAppts.filter(
        (a) => a.status === "completed",
    ).length;
    const scheduledAppts = sortedAppts.filter(
        (a) => a.status === "scheduled",
    ).length;
    const canceledAppts = sortedAppts.filter(
        (a) => a.status === "canceled",
    ).length;

    const navigateDate = (direction: "prev" | "next" | "today") => {
        const newDate = new Date(selectedDate);

        if (direction === "today") {
            setSelectedDate(new Date());
            return;
        }

        switch (view) {
            case "month":
                if (direction === "prev") {
                    newDate.setMonth(newDate.getMonth() - 1);
                } else {
                    newDate.setMonth(newDate.getMonth() + 1);
                }
                break;
            case "week":
                if (direction === "prev") {
                    newDate.setDate(newDate.getDate() - 7);
                } else {
                    newDate.setDate(newDate.getDate() + 7);
                }
                break;
            case "day":
                if (direction === "prev") {
                    newDate.setDate(newDate.getDate() - 1);
                } else {
                    newDate.setDate(newDate.getDate() + 1);
                }
                break;
        }
        setSelectedDate(newDate);
    };

    const handleDayClick = (date: Date) => {
        setSelectedDate(date);
        setView("day");
    };

    const handleAppointmentClick = (appointment: any) => {
        const appointmentDate = new Date(appointment.startTime);
        setSelectedDate(appointmentDate);
        setView("day");
    };

    const handleTimeSlotClick = (date: Date, hour: number) => {
        setModalDate(date);
        setModalTime(`${hour.toString().padStart(2, "0")}:00`);
        setModalBarberId(barbers[0]?._id ?? "");
        setModalClientId(clients[0]?._id ?? "");
        setModalService("Corte de cabelo");
        setModalDuration(60);
        setModalNotes("");
        setIsModalOpen(true);
    };

    const handleCreateAppointment = async () => {
        if (!modalBarberId || !modalClientId || !modalService.trim()) return;

        setIsSubmitting(true);
        try {
            const startMs = new Date(
                modalDate.getFullYear(),
                modalDate.getMonth(),
                modalDate.getDate(),
                parseInt(modalTime.split(":")[0] ?? "0"),
                parseInt(modalTime.split(":")[1] ?? "0"),
            ).getTime();
            const endMs = startMs + modalDuration * 60000;

            await createAppointment({
                barberId: modalBarberId as any,
                clientId: modalClientId as any,
                startTime: startMs,
                endTime: endMs,
                status: "scheduled" as any,
                service: modalService.trim(),
                notes: modalNotes || undefined,
            });

            setIsModalOpen(false);
            setModalNotes("");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Barber color system - using default emerald color for all
    const getBarberColors = (barberId: string, status: string) => {
        // Default emerald color scheme
        const colorScheme = {
            scheduled:
                "bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 text-emerald-900 dark:text-emerald-100 border-emerald-200 dark:border-emerald-600 shadow-emerald-100/50 dark:shadow-emerald-900/20",
            completed:
                "bg-gradient-to-br from-emerald-100 to-green-100 dark:from-emerald-800/50 dark:to-green-800/50 text-emerald-900 dark:text-emerald-100 border-emerald-300 dark:border-emerald-500 shadow-emerald-200/50 dark:shadow-emerald-800/20",
            canceled:
                "bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700 opacity-70",
        } as const;

        return (
            colorScheme[status as keyof typeof colorScheme] ||
            colorScheme.scheduled
        );
    };

    const getDisplayDate = () => {
        switch (view) {
            case "month":
                return selectedDate.toLocaleDateString("pt-BR", {
                    year: "numeric",
                    month: "long",
                });
            case "week":
                const startOfWeek = new Date(selectedDate);
                startOfWeek.setDate(
                    selectedDate.getDate() - selectedDate.getDay(),
                );
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                return `${startOfWeek.toLocaleDateString("pt-BR", { day: "numeric", month: "short" })} - ${endOfWeek.toLocaleDateString("pt-BR", { day: "numeric", month: "short", year: "numeric" })}`;
            case "day":
                return selectedDate.toLocaleDateString("pt-BR", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                });
            default:
                return "";
        }
    };

    return (
        <div className="h-full flex flex-col">
            {/* Integrated Header and Calendar */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden flex-1">
                {/* Header Section */}
                <div className="px-4 py-2 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-emerald-50 via-teal-50 to-blue-50 dark:from-emerald-900/20 dark:via-teal-900/20 dark:to-blue-900/20 backdrop-blur-sm">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2">
                        <div className="flex items-center gap-6">
                            <div></div>

                            {/* Quick Stats */}
                            <div className="hidden lg:flex items-center gap-4 text-sm">
                                <div className="flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-700/80 backdrop-blur-xl rounded-full border border-emerald-200/50 dark:border-emerald-600/50 shadow-lg">
                                    <div className="w-3 h-3 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full animate-pulse"></div>
                                    <span className="text-gray-800 dark:text-gray-200 font-semibold">
                                        {scheduledAppts} Agendados
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-700/80 backdrop-blur-xl rounded-full border border-green-200/50 dark:border-green-600/50 shadow-lg">
                                    <div className="w-3 h-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full"></div>
                                    <span className="text-gray-800 dark:text-gray-200 font-semibold">
                                        {completedAppts} Concluídos
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-700/80 backdrop-blur-xl rounded-full border border-red-200/50 dark:border-red-600/50 shadow-lg">
                                    <div className="w-3 h-3 bg-gradient-to-r from-red-400 to-pink-500 rounded-full"></div>
                                    <span className="text-gray-800 dark:text-gray-200 font-semibold">
                                        {canceledAppts} Cancelados
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3">
                            {/* Date Navigation */}
                            <div className="flex items-center gap-2 bg-white/90 dark:bg-gray-700/90 backdrop-blur-xl rounded-xl p-1 shadow-xl border border-gray-200/50 dark:border-gray-600/50">
                                <button
                                    onClick={() => navigateDate("prev")}
                                    className="p-2 rounded-lg hover:bg-gradient-to-r hover:from-emerald-50 hover:to-teal-50 dark:hover:from-emerald-900/20 dark:hover:to-teal-900/20 text-gray-700 dark:text-gray-300 transition-all duration-300 hover:scale-110 hover:shadow-lg"
                                    title={
                                        view === "month"
                                            ? "Mês anterior"
                                            : view === "week"
                                              ? "Semana anterior"
                                              : "Dia anterior"
                                    }
                                >
                                    <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2.5}
                                            d="M15 19l-7-7 7-7"
                                        />
                                    </svg>
                                </button>
                                <div className="px-3 py-1.5 rounded-lg font-bold text-gray-700 dark:text-gray-300 cursor-default select-none">
                                    {view === "month"
                                        ? selectedDate.toLocaleDateString(
                                              "pt-BR",
                                              {
                                                  month: "long",
                                                  year: "numeric",
                                              },
                                          )
                                        : view === "week"
                                          ? (() => {
                                                const start = new Date(
                                                    selectedDate,
                                                );
                                                start.setDate(
                                                    selectedDate.getDate() -
                                                        selectedDate.getDay(),
                                                );
                                                const end = new Date(start);
                                                end.setDate(
                                                    start.getDate() + 6,
                                                );
                                                return `${start.toLocaleDateString("pt-BR", { day: "numeric", month: "short" })} - ${end.toLocaleDateString("pt-BR", { day: "numeric", month: "short" })}`;
                                            })()
                                          : (() => {
                                                const today = new Date();
                                                const isToday =
                                                    selectedDate.toDateString() ===
                                                    today.toDateString();
                                                return isToday
                                                    ? "Hoje"
                                                    : selectedDate.toLocaleDateString(
                                                          "pt-BR",
                                                          {
                                                              day: "numeric",
                                                              month: "short",
                                                          },
                                                      );
                                            })()}
                                </div>
                                <button
                                    onClick={() => navigateDate("next")}
                                    className="p-2 rounded-lg hover:bg-gradient-to-r hover:from-emerald-50 hover:to-teal-50 dark:hover:from-emerald-900/20 dark:hover:to-teal-900/20 text-gray-700 dark:text-gray-300 transition-all duration-300 hover:scale-110 hover:shadow-lg"
                                    title={
                                        view === "month"
                                            ? "Próximo mês"
                                            : view === "week"
                                              ? "Próxima semana"
                                              : "Próximo dia"
                                    }
                                >
                                    <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2.5}
                                            d="M9 5l7 7-7 7"
                                        />
                                    </svg>
                                </button>
                            </div>

                            <div className="flex items-center bg-white/90 dark:bg-gray-700/90 backdrop-blur-xl rounded-xl p-1 shadow-xl border border-gray-200/50 dark:border-gray-600/50 min-w-[220px]">
                                <select
                                    value={barberId}
                                    onChange={(e) =>
                                        setBarberId(e.target.value)
                                    }
                                    className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-0 focus:border-transparent w-full font-bold appearance-none cursor-pointer shadow-sm"
                                >
                                    <option value="todos">
                                        Todos os barbeiros
                                    </option>
                                    {barbers.map((b) => (
                                        <option
                                            key={b._id}
                                            value={b._id as string}
                                        >
                                            {b.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* View Switcher (rightmost) */}
                            <div className="sm:ml-auto flex items-center bg-white/90 dark:bg-gray-700/90 backdrop-blur-xl rounded-xl p-1 shadow-xl border border-gray-200/50 dark:border-gray-600/50">
                                {(["month", "week", "day"] as ViewType[]).map(
                                    (v) => (
                                        <button
                                            key={v}
                                            onClick={() => setView(v)}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all duration-300 ${
                                                view === v
                                                    ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg transform scale-105 shadow-emerald-200/50 dark:shadow-emerald-900/30"
                                                    : "text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-teal-50 dark:hover:from-emerald-900/20 dark:hover:to-teal-900/20 hover:scale-102"
                                            }`}
                                        >
                                            {v === "month"
                                                ? "Mês"
                                                : v === "week"
                                                  ? "Semana"
                                                  : "Dia"}
                                        </button>
                                    ),
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Calendar Views */}
                <div className="flex-1 transition-all duration-300 ease-in-out">
                    {view === "month" && (
                        <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                            <MonthView
                                selectedDate={selectedDate}
                                appointments={sortedAppts}
                                clientsById={clientsById}
                                barbersById={barbersById}
                                onDayClick={handleDayClick}
                                onAppointmentClick={handleAppointmentClick}
                                getBarberColors={getBarberColors}
                            />
                        </div>
                    )}
                    {view === "week" && (
                        <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                            <WeekView
                                selectedDate={selectedDate}
                                appointments={sortedAppts}
                                clientsById={clientsById}
                                barbersById={barbersById}
                                onAppointmentClick={handleAppointmentClick}
                                getBarberColors={getBarberColors}
                            />
                        </div>
                    )}
                    {view === "day" && (
                        <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                            <DayView
                                selectedDate={selectedDate}
                                appointments={sortedAppts}
                                clientsById={clientsById}
                                barbersById={barbersById}
                                onAppointmentClick={handleAppointmentClick}
                                onTimeSlotClick={handleTimeSlotClick}
                                getBarberColors={getBarberColors}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Appointment Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                        Novo Agendamento
                                    </h2>
                                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                                        {modalDate.toLocaleDateString("pt-BR", {
                                            weekday: "long",
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                        })}{" "}
                                        às {modalTime}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <svg
                                        className="w-6 h-6"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>

                            <div className="space-y-4">
                                {/* Cliente e Barbeiro */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Cliente
                                        </label>
                                        <select
                                            value={modalClientId}
                                            onChange={(e) =>
                                                setModalClientId(e.target.value)
                                            }
                                            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                                        >
                                            {clients.map((c) => (
                                                <option
                                                    key={c._id}
                                                    value={c._id as string}
                                                >
                                                    {c.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Barbeiro
                                        </label>
                                        <select
                                            value={modalBarberId}
                                            onChange={(e) =>
                                                setModalBarberId(e.target.value)
                                            }
                                            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                                        >
                                            {barbers.map((b) => (
                                                <option
                                                    key={b._id}
                                                    value={b._id as string}
                                                >
                                                    {b.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Horário */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Horário
                                    </label>
                                    <input
                                        type="time"
                                        value={modalTime}
                                        onChange={(e) =>
                                            setModalTime(e.target.value)
                                        }
                                        className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                                    />
                                </div>

                                {/* Serviço e Duração */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Serviço
                                        </label>
                                        <select
                                            value={modalService}
                                            onChange={(e) =>
                                                setModalService(e.target.value)
                                            }
                                            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                                        >
                                            {serviceOptions.map((option) => (
                                                <option
                                                    key={option}
                                                    value={option}
                                                >
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Duração
                                        </label>
                                        <select
                                            value={modalDuration}
                                            onChange={(e) =>
                                                setModalDuration(
                                                    Number(e.target.value),
                                                )
                                            }
                                            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                                        >
                                            <option value={15}>15 min</option>
                                            <option value={30}>30 min</option>
                                            <option value={45}>45 min</option>
                                            <option value={60}>1h</option>
                                            <option value={90}>1h 30min</option>
                                            <option value={120}>2h</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Observações */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Observações{" "}
                                        <span className="text-gray-400">
                                            (opcional)
                                        </span>
                                    </label>
                                    <textarea
                                        value={modalNotes}
                                        onChange={(e) =>
                                            setModalNotes(e.target.value)
                                        }
                                        placeholder="Ex: Cliente preferiu barba mais curta..."
                                        rows={3}
                                        className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors resize-none"
                                    />
                                </div>

                                {/* Resumo */}
                                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 border border-emerald-200 dark:border-emerald-800">
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                                        <div className="flex items-center gap-2">
                                            <span className="text-emerald-700 dark:text-emerald-300 font-medium">
                                                {clients.find(
                                                    (c) =>
                                                        c._id === modalClientId,
                                                )?.name || "Cliente"}
                                            </span>
                                            <span className="text-emerald-600 dark:text-emerald-400">
                                                •
                                            </span>
                                            <span className="text-emerald-700 dark:text-emerald-300">
                                                {barbers.find(
                                                    (b) =>
                                                        b._id === modalBarberId,
                                                )?.name || "Barbeiro"}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                                                {modalService}
                                            </span>
                                            <span className="text-emerald-600 dark:text-emerald-400">
                                                •
                                            </span>
                                            <span className="text-emerald-700 dark:text-emerald-300">
                                                {modalDuration}min
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Botões */}
                                <div className="flex justify-end gap-3 pt-4">
                                    <button
                                        onClick={() => setIsModalOpen(false)}
                                        className="px-4 py-2.5 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleCreateAppointment}
                                        disabled={
                                            isSubmitting ||
                                            !modalBarberId ||
                                            !modalClientId ||
                                            !modalService.trim()
                                        }
                                        className="px-6 py-2.5 bg-emerald-500 text-white font-medium rounded-lg hover:bg-emerald-600 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <svg
                                                    className="w-4 h-4 animate-spin"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                                    />
                                                </svg>
                                                Agendando...
                                            </>
                                        ) : (
                                            <>
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                                    />
                                                </svg>
                                                Agendar
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
